// src/lib/customerDisplay.ts
import { Capacitor } from "@capacitor/core";
import { CustomerDisplay as NativeCustomerDisplay } from "../plugins/customerDisplay";

type AnyMsg = any;

const STORAGE_KEY = "forkcast:customer_display:last_message";
const CHANNEL_NAME = "forkcast_customer_display";

function safeJsonParse<T = any>(s: string | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

// WEB only: opens a popup route for customer display
export function openCustomerDisplay() {
  try {
    const url = "/customer-display?screen=customer";
    const w = window.open(url, "customerDisplay", "width=1024,height=768");
    return w;
  } catch (e) {
    console.warn("openCustomerDisplay failed:", e);
    return null;
  }
}

/**
 * Send a message to customer display:
 * - Android native (Presentation WebView): CustomerDisplay.send({ message })
 *   - Fallback: localStorage bus
 * - Web: BroadcastChannel (if available) + popup postMessage (if window provided)
 *
 * IMPORTANT: this function MUST NEVER throw (Till calls it frequently).
 */
export function sendToCustomerDisplay(msg: AnyMsg, popupWindow?: Window | null) {
  try {
    // 1) Native Android: use the plugin "send" to inject MessageEvent into the Presentation WebView
    if (Capacitor.getPlatform() === "android") {
      // Fire and forget (never block UI)
      NativeCustomerDisplay.send({ message: msg })
        .catch((e) => {
          // If Presentation webview not ready yet, fallback to storage bus
          console.warn("Native CustomerDisplay.send failed, fallback to storage bus:", e);
          try {
            const payload = { t: Date.now(), msg };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
          } catch (err) {
            console.warn("localStorage fallback failed:", err);
          }
        });
      return;
    }

    // If some other native platform shows up later, keep old behaviour as a safe fallback
    if (Capacitor.isNativePlatform()) {
      try {
        const payload = { t: Date.now(), msg };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {
        console.warn("localStorage bus failed:", e);
      }
      return;
    }

    // 2) Web: BroadcastChannel (if supported)
    try {
      if (typeof BroadcastChannel !== "undefined") {
        const bc = new BroadcastChannel(CHANNEL_NAME);
        bc.postMessage(msg);
        bc.close();
      }
    } catch (e) {
      console.warn("BroadcastChannel send failed:", e);
    }

    // 3) Web popup: postMessage directly if we have the window
    try {
      if (popupWindow && !popupWindow.closed) {
        popupWindow.postMessage(msg, "*");
      }
    } catch (e) {
      console.warn("popup postMessage failed:", e);
    }
  } catch (e) {
    // absolute last-resort: never crash caller
    console.warn("sendToCustomerDisplay unexpected error:", e);
  }
}

/**
 * Listen for customer display messages:
 * - Web: BroadcastChannel + window message events
 * - Android Presentation: plugin injects window MessageEvent('message')
 * - Fallback on native: localStorage polling bus
 *
 * Returns cleanup fn.
 */
export function listenCustomerDisplay(onMsg: (msg: AnyMsg) => void) {
  let bc: BroadcastChannel | null = null;
  let pollTimer: number | null = null;
  let lastSeenT = 0;

  const onWindowMessage = (ev: MessageEvent) => {
    try {
      if (!ev?.data) return;
      onMsg(ev.data);
    } catch (e) {
      console.warn("window message handler error:", e);
    }
  };

  // window.postMessage listener (works in web + native injection)
  try {
    window.addEventListener("message", onWindowMessage as any);
  } catch (e) {
    console.warn("addEventListener(message) failed:", e);
  }

  // Web BroadcastChannel listener
  if (!Capacitor.isNativePlatform()) {
    try {
      if (typeof BroadcastChannel !== "undefined") {
        bc = new BroadcastChannel(CHANNEL_NAME);
        bc.onmessage = (ev) => {
          try {
            onMsg(ev.data);
          } catch (e) {
            console.warn("BroadcastChannel onmessage error:", e);
          }
        };
      }
    } catch (e) {
      console.warn("BroadcastChannel listen failed:", e);
    }
  }

  // Native fallback bus: poll localStorage (only needed if plugin injection fails for any reason)
  if (Capacitor.isNativePlatform()) {
    const poll = () => {
      try {
        const obj = safeJsonParse<{ t: number; msg: AnyMsg }>(localStorage.getItem(STORAGE_KEY));
        if (!obj) return;
        if (typeof obj.t !== "number") return;
        if (obj.t <= lastSeenT) return;
        lastSeenT = obj.t;
        onMsg(obj.msg);
      } catch (e) {
        console.warn("localStorage poll error:", e);
      }
    };

    pollTimer = window.setInterval(poll, 150);
    poll();
  }

  return () => {
    try {
      window.removeEventListener("message", onWindowMessage as any);
    } catch {}

    try {
      if (bc) bc.close();
    } catch {}

    try {
      if (pollTimer != null) window.clearInterval(pollTimer);
    } catch {}
  };
}
